
import { defineComponent } from 'vue'

import { centsToDollars } from '~/utils/billing'
import { formatDate, getHumanizedTimeFromNow, parseISODate } from '~/utils/date'

import ActiveUserMixin from '~/mixins/activeUserMixin'
import OwnerBillingMixin from '~/mixins/ownerBillingMixin'
import PlanDetailMixin from '~/mixins/planDetailMixin'

import GenerateSCACheckoutURL from '~/apollo/mutations/owner/generateCheckoutUrlForSca.gql'
import GenerateSCAManagementURL from '~/apollo/mutations/owner/generateManagementUrlForSca.gql'
import MigrateToUnifiedBillingMutation from '~/apollo/mutations/owner/migrateToUnifiedBilling.gql'
import SCABillingQuery from '~/apollo/queries/owner/scaBilling.gql'

import { TEAM_PLAN } from '~/components/Billing/V2/v2PlanConfig'

import { GraphqlMutationResponse, GraphqlQueryResponse } from '~/types/apollo-graphql-types'
import {
  BillingBackend,
  BillingInterval,
  BillingManagementFlow,
  ProductSubscription,
  ScaProduct,
  ScaProductPlan
} from '~/types/types'

export default defineComponent({
  name: 'BillingV1Page',
  mixins: [ActiveUserMixin, PlanDetailMixin, OwnerBillingMixin],
  data() {
    return {
      seatsLoading: false,
      seatsUpdateLoading: false,
      cancelSCALoading: false,
      intervalUpdateLoading: false,
      loading: true,
      billingEmail: '',
      billingAddress: '',
      showUpgradePlanModal: false,
      showUpdateSeatsModal: false,
      showPlanChangeModal: false,
      seatsCount: 0,
      scaProductSubscription: null as ProductSubscription | null,
      scaSelectedBillingInterval: BillingInterval.Monthly as BillingInterval,
      showMigrateModal: false,
      isMigrating: false,
      isEligibleForMigration: false
    }
  },
  async fetch(): Promise<void> {
    const { owner, provider } = this.$route.params
    const params = {
      login: owner,
      provider
    }
    this.loading = true
    await Promise.allSettled([
      this.fetchOwnerDetails(params),
      this.fetchBillingDetails(params),
      this.fetchSCABillingDetails(params),
      this.checkMigrationEligibility()
    ])
    this.loading = false
    this.seatsCount = Number(this.ownerBillingInfo.seatsTotal)
    this.billingEmail = this.owner.billingInfo?.billingEmail || ''
    this.billingAddress = this.owner.billingInfo?.billingAddress || ''
  },
  methods: {
    formatDate,
    parseISODate,
    async refetch(): Promise<void> {
      this.loading = true
      await this.refetchUser()
      this.$root.$emit('billing:plan-changed')
      this.loading = false
    },
    async fetchSCABillingDetails(params: any) {
      try {
        const response = (await this.$fetchGraphqlData(SCABillingQuery, {
          login: params.login,
          provider: this.$providerMetaMap[params.provider].value
        })) as GraphqlQueryResponse

        this.scaProductSubscription = response.data?.owner?.productSubscription ?? null
      } catch (error: any) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while fetching your billing details. Please try again later.'
        )
        return null
      }
      return null
    },
    handleCQUpgrade(slug: string) {
      if (this.currentPlan.slug === 'free' || !this.currentPlan.slug) {
        this.setPlan({
          selectedPlan: slug
        })
        this.$router.push(
          this.$generateRoute(['settings', 'billing', 'subscribe', slug], false, true)
        )
      } else {
        this.showUpgradePlanModal = true
      }
    },
    async onSCASeatsUpdate() {
      this.seatsUpdateLoading = true
      const url = await this.handleSCAManagement(BillingManagementFlow.SubscriptionUpdate)
      if (url) {
        window.location.href = url
      }
    },
    async onSCAIntervalUpdate() {
      this.intervalUpdateLoading = true
      const url = await this.handleSCAManagement(BillingManagementFlow.SubscriptionUpdate)
      if (url) {
        window.location.href = url
      }
    },
    async onSCACancelSubscription() {
      this.cancelSCALoading = true
      const url = await this.handleSCAManagement(BillingManagementFlow.SubscriptionCancel)
      if (url) {
        window.location.href = url
      }
      this.cancelSCALoading = false
    },
    async handleSCAManagement(flow: BillingManagementFlow) {
      if (!this.scaProductSubscription) return
      try {
        const subscriptionID = this.scaProductSubscription.id
        if (!subscriptionID) {
          throw new Error('invalid subscription id sca product subscription')
        }
        const response = (await this.$applyGraphqlMutation(GenerateSCAManagementURL, {
          subscriptionId: subscriptionID,
          flow
        })) as GraphqlMutationResponse
        const url = response?.data.getSubscriptionManagementUrlForSca?.url
        if (url) {
          return url
        } else {
          throw new Error('invalid management url response from server')
        }
      } catch (error) {
        this.$logErrorAndToast(error as Error, 'We encountered an error. Please try again later.')
      }
    },
    async handleSCAUpgrade() {
      try {
        const response = (await this.$applyGraphqlMutation(GenerateSCACheckoutURL, {
          ownerId: this.owner.id,
          plan: ScaProductPlan.PlanStarter,
          interval: this.scaSelectedBillingInterval
        })) as GraphqlMutationResponse
        const url = response?.data?.getSubscriptionCheckoutUrlForSca?.url
        if (url) {
          window.location.href = url
        } else {
          throw new Error('invalid checkout url response from server')
        }
      } catch (error) {
        this.$logErrorAndToast(error as Error, 'We encountered an error. Please try again later.')
      }
    },
    async updateDetails(): Promise<void> {
      const { owner, provider } = this.$route.params
      try {
        await this.updateBillingInfo({
          billingEmail: this.billingEmail,
          billingAddress: this.billingAddress,
          login: owner,
          provider
        })
        this.$toast.success('Billing details updated successfully')
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while updating your billing details. Please try again later.'
        )
      }
    },
    async onUpdateSeats(close?: () => void) {
      const { owner, provider } = this.$route.params
      this.seatsLoading = true
      if (this.isBilledByStripe) {
        if (this.seatsCount === Number(this.ownerBillingInfo.seatsTotal)) {
          this.$toast.success('Seat count updated successfully.')
          this.seatsLoading = false
          return
        }

        try {
          await this.updateSeats({
            id: this.owner.id,
            seats: this.seatsCount
          })
          await this.fetchBillingDetails({
            login: owner,
            provider,
            refetch: true
          })
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'We encountered an error while updating your billing details. Please try again later.'
          )
        } finally {
          this.seatsLoading = false
          this.showUpdateSeatsModal = false
          close?.()
        }
      }
    },
    handleBillingIntervalUpdate(value: string) {
      this.scaSelectedBillingInterval =
        value === 'annual' ? BillingInterval.Annual : BillingInterval.Monthly
    },
    getScaUpcomingAmount(amount?: number | null): number | undefined {
      return typeof amount === 'number' ? centsToDollars(amount) : undefined
    },
    async checkMigrationEligibility(): Promise<void> {
      if (!this.owner?.id) return
      try {
        const response = await this.$applyGraphqlMutation(MigrateToUnifiedBillingMutation, {
          ownerId: this.owner.id,
          confirm: false
        })
        this.isEligibleForMigration = Boolean(response?.data?.migrateToUnifiedBilling?.ok)
      } catch (e) {
        this.isEligibleForMigration = false
        this.$sentry.captureException(e as Error, {
          tags: {
            feature: 'billing-migration',
            action: 'check-migration-eligibility',
            ownerId: this.owner.id
          }
        })
      }
    },
    async handleMigratePlan(): Promise<void> {
      this.isMigrating = true
      try {
        const response = await this.$applyGraphqlMutation(MigrateToUnifiedBillingMutation, {
          ownerId: this.owner.id,
          confirm: true
        })

        const result = response?.data?.migrateToUnifiedBilling
        if (result?.ok) {
          this.$toast.success('Successfully migrated to the Team plan.')
          this.showMigrateModal = false
          this.isEligibleForMigration = false
          await this.refetch()
        } else {
          this.$toast.danger(result?.error || 'Something went wrong while migrating your plan.')
        }
      } catch (e: unknown) {
        this.$logErrorAndToast(e as Error, 'Something went wrong while migrating your plan.')
      } finally {
        this.isMigrating = false
      }
    }
  },
  computed: {
    isSCABilledManually(): boolean {
      return this.scaProductSubscription?.billingBackend === BillingBackend.Manual
    },
    BillingInterval() {
      return BillingInterval
    },
    BillingManagementFlow() {
      return BillingManagementFlow
    },
    scaTargetQuantity(): number {
      const product = this.scaProductSubscription?.product as ScaProduct
      return product?.targetQuantity || 0
    },
    scaUpgradePlans(): ScaProductPlan[] {
      const currentPlan = this.scaCurrentPlan
      const order = [ScaProductPlan.PlanStarter]
      const currentPlanIndex = order.indexOf(currentPlan)
      if (currentPlanIndex === -1) return order
      return order.slice(currentPlanIndex + 1)
    },
    scaCurrentPlan(): ScaProductPlan {
      const plan = this.scaProductSubscription?.product?.plan as ScaProductPlan
      if (!plan) return ScaProductPlan.PlanFree
      return plan
    },
    cancellationDate(): string | undefined {
      if (this.owner.billingInfo)
        return getHumanizedTimeFromNow(this.owner.billingInfo.upcomingCancellationDate)
      return undefined
    },
    isV1StarterPlan(): boolean {
      return Boolean(this.ownerBillingInfo?.planSlug?.includes('starter'))
    },
    isV1BusinessPlan(): boolean {
      const slug = this.ownerBillingInfo?.planSlug
      return Boolean(slug?.includes('premium') || slug?.includes('business'))
    },
    currentPlanPriceLabel(): string {
      const isAnnual = this.ownerBillingInfo?.planSlug?.includes('annual')
      if (this.isV1StarterPlan) {
        return isAnnual ? '$8/seat/mo, billed annually' : '$10/seat/mo, billed monthly'
      }
      const price = isAnnual ? TEAM_PLAN.annualPrice : TEAM_PLAN.price
      return isAnnual ? `${price}, billed annually` : `${price}, billed monthly`
    },
    newPlanPriceLabel(): string {
      const isAnnual = this.ownerBillingInfo?.planSlug?.includes('annual')
      if (this.isV1StarterPlan) {
        return isAnnual
          ? '$24/contributor/mo, billed annually'
          : '$30/contributor/mo, billed monthly'
      }
      const price = isAnnual ? TEAM_PLAN.annualPrice : TEAM_PLAN.price
      return isAnnual ? `${price}, billed annually` : `${price}, billed monthly`
    },
    cqUpgradePlans() {
      // V1 Starter and Business users should not see cross-plan upgrade cards
      if (this.isV1StarterPlan || this.isV1BusinessPlan) return []

      let plans = []
      // Show all plan cards for the free tier
      if (!this.activeDashboardContext.has_premium_plan) {
        plans = this.plansInContext
      } else {
        const idx = this.plansInContext.findIndex((plan) => plan === this.currentPlan.slug)
        if (idx === -1) {
          return this.plansInContext.filter((plan) => plan !== 'enterprise')
        }
        plans = this.plansInContext.slice(idx + 1)
      }
      return plans.filter((plan) => plan !== 'enterprise')
    },
    cqMonthlyAmount(): number | undefined {
      const slug = this.currentPlan.slug
      return this.planDetails[slug]?.monthlyAmount
    },
    cqAnnualAmount(): number | undefined {
      const slug = this.currentPlan.slug
      return this.planDetails[slug]?.annualAmount
    },
    cqPlanAlternateInterval(): string {
      return `plan-${this.currentPlan.slug}-${
        this.currentPlan.mode === this.MODE.ANNUAL ? 'monthly' : 'annual'
      }`
    }
  }
})
